import type {
  Communication,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { getType, isOfType } from '@seen/core/utils'
import { useMemo } from 'react'
import { useRead } from '#src/hooks/useRead.ts'
import { useResource } from '#src/hooks/useResource.ts'

export interface UseCommentParentResourceResult {
  comment: CommentCommunication | undefined
  /* The raw resolved parent resource (Task, ServiceRequest, DocumentReference, etc.) */
  resource: Resource | undefined
  /* Synchronous resource type derived from the reference string (available before resource loads) */
  parentResourceType: ResourceType | undefined
  relatedTask: StandardTask | undefined
  relatedOrder: Order | undefined
  patient: Participant | undefined
  isLoading: boolean
}

/*
 * Resolves a comment reference to its parent resource (task, order, etc.)
 * by walking the partOf chain. Handles reply threads where partOf points
 * to another Communication rather than directly to a resource.
 */
export function useCommentParentResource(
  commentRef: Reference<Communication> | undefined
): UseCommentParentResourceResult {
  const { data: comment, isLoading: isLoadingComment } = useRead(
    CommentCommunication,
    commentRef
  )

  /* Resolve the first partOf -- may be a resource or another comment */
  const { data: parentCommentOrResource, isLoading: isLoadingPartOf } =
    useResource(comment?.partOf?.[0])

  /*
   * If the partOf is itself a comment (reply thread), resolve one level up
   * to get the actual resource (Task, Order, etc.)
   */
  const { data: maybeResource, isLoading: isLoadingResource } = useResource(
    CommentCommunication.isInstance(parentCommentOrResource)
      ? parentCommentOrResource.partOf?.[0]
      : undefined
  )

  const resource = maybeResource ?? parentCommentOrResource

  /*
   * Derive the parent resource type synchronously from the reference string.
   * When the partOf is another Communication (reply thread), resolve one
   * level further. This avoids a loading flash in row components that need
   * the type for rendering structure before the resource loads.
   */
  const parentResourceType = useMemo(() => {
    const partOfRef = comment?.partOf?.[0]
    if (!partOfRef) {
      return undefined
    }
    const directType = getType(partOfRef)
    if (
      directType === 'Communication' &&
      CommentCommunication.isInstance(parentCommentOrResource)
    ) {
      return getType(parentCommentOrResource.partOf?.[0])
    }
    return directType
  }, [comment?.partOf, parentCommentOrResource])

  const relatedTask = useMemo(() => {
    if (isOfType(resource, 'Task')) {
      return StandardTask.fromFhirResource(resource)
    }
    return undefined
  }, [resource])

  const relatedOrder = useMemo(() => {
    if (isOfType(resource, 'ServiceRequest')) {
      return Order.fromFhirResource(resource)
    }
    return undefined
  }, [resource])

  const { data: patient } = useRead(
    Participant,
    relatedTask?.participant ?? relatedOrder?.participant
  )

  const isLoading = isLoadingComment || isLoadingPartOf || isLoadingResource

  return {
    comment,
    resource,
    parentResourceType,
    relatedTask,
    relatedOrder,
    patient,
    isLoading,
  }
}
